import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePersonDetailContext } from '../../contexts/PersonDetailContext';
import { useKeycloak } from '../../contexts/KeycloakContext';
import { isAdmin, isManager } from '../../config/roles';
import { X, ChevronLeft, ChevronRight, User, Shield, Clock, Camera, Car, Tag, FileText, ExternalLink, RefreshCw, UserPlus, AlertCircle, Calendar, History, MessageSquare, Copy, Check, ChevronDown, ChevronUp, HeartPulse, Fingerprint, ShieldOff, Loader2, LogIn, LogOut } from 'lucide-react';
import { OverviewTab } from './tabs/OverviewTab';
import { PermissionsTab } from './tabs/PermissionsTab';
import { AccessHistoryTab } from './tabs/AccessHistoryTab';
import { IdentityPhotosTab } from './tabs/IdentityPhotosTab';
import { VehiclesTab } from './tabs/VehiclesTab';
import { TagsTab } from './tabs/TagsTab';
import { DocumentsTab } from './tabs/DocumentsTab';
import { NotesTab } from './tabs/NotesTab';
import { ASOTab } from './tabs/ASOTab';
import { BiometriaTab } from './tabs/BiometriaTab';
import { VisitorStatusCard } from '../VisitorStatusCard';
import CountdownTimer from '../CountdownTimer';
import VisitorRegistrationModal from '../VisitorRegistrationModal';
import { ModificationHistoryModal } from '../ModificationHistoryModal';
import { OperationalConfirmModal } from '../OperationalView/components/OperationalConfirmModal';
import { RevokeReasonModal } from '../OperationalView/components/RevokeReasonModal';
import { ReentryConfirmationModal } from '../OperationalView/components/ReentryConfirmationModal';
import { useOperationalSettings } from '../../contexts/OperationalSettingsContext';
import { api, condoApi, fetchWithAuth } from '../../services/api';
import { logger } from '../../services/logger';
import { DeviceInfo } from '../../services/deviceCommunication';
import { DeviceIntegration, DeviceOperations, CommunicationMode, fetchPersonDeviceHandles } from '../../integrations/devices';
import { translatePersonTypeToPortuguese } from '../../utils/terminology';
import { getAbsoluteImageUrl } from '../../utils/imageUrl';
import { buildPhotoGallery, PhotoGalleryItem } from './photoGallery';
import { PhotoCaptureFlow, PhotoCaptureFlowHandle } from './PhotoCaptureFlow';
import { getPrimaryPhotoUrl } from '../../utils/photoUtils';
import { getPersonPhoto } from '../../utils/personPhotoCache';
import { cn } from '../../utils/cn';
import { ManualAccessDialog } from './ManualAccessDialog';
import { ManualAccessSelection, manualAccessDescription, manualAccessPayload } from '../../utils/manualAccessMode';
import { fetchPersonDevices, removePersonFromDevices, revokeEntryDevices, revokeExitDevices } from '../../utils/deviceRevoke';
import { isVisitorActiveToday, getVisitorStatus } from '../../utils/visitorUtils';
import { resolveDeviceSyncTimes, buildPersonSyncPayload } from '../../domain/personSyncData';
import { groupDeviceFailures, formatSyncSuccess } from '../../utils/deviceSyncError';
import { deviceFailureToast } from '../DeviceFailureToast';
import { syncDeviceAndReport } from '../../utils/syncDeviceAndReport';
import { useSyncReporter } from '../../hooks/useSyncReporter';
import {
  canEditPhotos as canEditPhotosRule,
  resolveReadOnly,
  shouldShowNovaVisita as shouldShowNovaVisitaRule,
  shouldShowASOTab,
} from './personModalRules';
import { reportDeviceSyncOutcome } from '../../utils/deviceSyncOutcome';
import toast from 'react-hot-toast';

export interface PersonDetailData {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  employee_no?: string;
  document_no?: string;
  cpf?: string; // Brazilian CPF
  rg?: string;  // Brazilian RG
  /** Foreigner identity — see utils/foreigner.ts. */
  is_foreigner?: boolean;
  passport_number?: string;
  /** ISO 3166-1 alpha-2. */
  country?: string;
  date_of_birth?: string;
  is_adult?: boolean;
  // Privacy: when true, other residents of this person's unit cannot see this person's
  // individual access events in their unit-scoped "meus eventos" view. Admin/super_admin
  // only — see PATCH .../people/:person_id/history-privacy.
  hide_access_history_from_unit?: boolean;
  keycloak_user_id?: string; // Linked Keycloak user account ID
  identity_photo_url?: string;
  profile_picture_url?: string;
  document_front_url?: string;
  document_back_url?: string;
  document_type?: string;
  document_number?: string;
  is_active: boolean;
  time_zone_id?: string;
  created_at?: string;      // ISO 8601 timestamp
  updated_at?: string;      // ISO 8601 timestamp
  created_by?: string;      // User ID who created this person
  created_by_name?: string; // Name of user who created (if available)
  roles?: Array<{
    id: string;
    role_type: string;
    valid_from: string;
    valid_until?: string;
    is_active: boolean;
    is_authorized: boolean;
    role_metadata?: {
      unit_id?: string;
      unit_number?: string;
      block_name?: string;
      unit_info?: {
        id: string;
        number: string;
        block: {
          id: string;
          name: string;
        };
      };
      department_id?: string;
      department_name?: string;
      company_name?: string;
      service_type?: string;
      requires_intercom?: boolean;
      [key: string]: any;
    };
  }>;
  identity_photos?: Array<{
    id: string;
    photo_url: string;
    // The untouched upload behind the square crop in photo_url. Absent on rows written
    // before migration 230 — their originals were destroyed in the browser.
    original_photo_url?: string;
    is_primary: boolean;
    created_at: string;
    photo_type?: string;
  }>;
  vehicles?: Array<{
    id: string;
    plate: string;
    rfid_tag?: string;
    brand?: string;
    model?: string;
    color?: string;
    year?: number;
    owner_type?: string;
    owner_name?: string;
    unit_id?: string;
    photo_url?: string;
    notes?: string;
    is_active: boolean;
    created_at: string;
  }>;
  extra_data?: Record<string, any>;
  customer?: {
    id: string;
    name: string;
    type?: 'condominio' | 'empresa';
    condo_data?: {
      condo_type?: 'apartamentos' | 'casas';
      aso_enabled?: boolean;
      aso_applies_to?: string[];
      aso_block_access?: boolean;
      time_zones_enabled?: boolean;
      // Who may liberar visitantes — read by the Permissões tab to decide whether a per-person
      // exception is even meaningful. See pages/visitorAuthorizer.ts.
      visitor_authorization_control_enabled?: boolean;
      visitor_authorizer_relations?: string[];
    };
    empresa_data?: {
      visitor_authorization_control_enabled?: boolean;
      visitor_authorizer_relations?: string[];
      departments?: Array<{
        id: string;
        name: string;
      }>;
      time_zones_enabled?: boolean;
    };
  };
}

type TabType = 'overview' | 'permissions' | 'history' | 'photos' | 'documents' | 'vehicles' | 'tags' | 'notes' | 'aso' | 'biometria';

export const PersonDetailModal: React.FC = () => {
  const navigate = useNavigate();
  const { isOpen, personId, readOnly, darkMode: darkModeFromContext, openPersonDetail, closePersonDetail, onVisitProfile, onUnitClick, onDepartmentClick, onPersonUpdate, setOnPersonUpdate, visitorActionHandlers } = usePersonDetailContext();
  const { settings: opSettings } = useOperationalSettings();
  // Light operational theme always overrides darkMode regardless of how the modal was opened
  const darkMode = opSettings.theme === 'light' ? false : darkModeFromContext;
  const { roles, user } = useKeycloak();
  // Progress presentation: the sync card in the toast corner (zoomable to the modal).
  const makeSyncReporter = useSyncReporter();
  const operatorName = user?.name || user?.username || 'Operador';
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [triggerPhotoAdd, setTriggerPhotoAdd] = useState(false);
  const [personData, setPersonData] = useState<PersonDetailData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [pendingTab, setPendingTab] = useState<TabType | null>(null);

  // Revoke reason modal state
  const [revokeModal, setRevokeModal] = useState<{ isOpen: boolean; visitor: any | null }>({
    isOpen: false,
    visitor: null,
  });

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    confirmButtonClass: string;
    icon: 'warning' | 'entry' | 'exit' | 'revoke';
    onConfirm: () => void;
    isProcessing: boolean;
    showDisclaimer?: boolean;
    disclaimerType?: 'entry' | 'revoke' | 'general';
    operatorName?: string;
    requireAcknowledgment?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirmar',
    confirmButtonClass: 'bg-blue-600 hover:bg-blue-700',
    icon: 'warning',
    onConfirm: () => {},
    isProcessing: false,
  });

  // Sync status state (persists across tab changes)
  const [syncStatus, setSyncStatus] = useState<{
    isSyncing: boolean;
    lastSyncAt: Date | null;
    syncResult: 'success' | 'error' | 'partial' | null;
    syncMessage: string;
    successfulDevices?: { device_id: string; device_name: string }[];
    failedDevices?: { device_id: string; device_name: string; error: string }[];
  }>({
    isSyncing: false,
    lastSyncAt: null,
    syncResult: null,
    syncMessage: '',
  });

  // Photo viewer state
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [manualAccess, setManualAccess] = useState<{
    open: boolean;
    direction: 'entrada' | 'saida';
    roleId: string | null;
    isProcessing: boolean;
  }>({ open: false, direction: 'entrada', roleId: null, isProcessing: false });
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [lightboxPhotoLoaded, setLightboxPhotoLoaded] = useState(false);
  // Set when the original cannot be fetched, so the lightbox falls back to the crop.
  const [lightboxOriginalFailed, setLightboxOriginalFailed] = useState(false);
  const [sidebarPhotoLoaded, setSidebarPhotoLoaded] = useState(false);
  const [photoGallery, setPhotoGallery] = useState<PhotoGalleryItem[]>([]);

  // Lightbox inline edit
  const photoFlowRef = useRef<PhotoCaptureFlowHandle>(null);

  // Visitor statistics state
  const [visitorStats, setVisitorStats] = useState<{
    last_visit_date?: string;
    total_visits: number;
    current_status: 'active' | 'inactive' | 'never_visited';
    active_visit?: {
      id: string;
      check_in_time: string;
      unit_number?: string;
      block_name?: string;
      access_expires_at?: string;
      valid_until?: string;
    };
  } | null>(null);

  // Control for VisitorRegistrationModal
  const [showVisitorModal, setShowVisitorModal] = useState(false);

  // Control for ReentryConfirmationModal
  const [showReentryConfirmation, setShowReentryConfirmation] = useState(false);
  const [reentryVisitorInfo, setReentryVisitorInfo] = useState<{
    personId: string;
    roleId: string;
    personName: string;
    unit: string;
    entryTime: string;
    personData?: any;
  } | null>(null);

  // User ID display state
  // Control for ModificationHistoryModal
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Calculate effective readOnly based on user role
  // Admins and Managers (sindico, supervisor, condo_manager) can always edit photos
  const canEditPhotos = useMemo(() => canEditPhotosRule(roles), [roles]);

  // If user has permission to edit, override readOnly for photos
  const effectiveReadOnly = resolveReadOnly(readOnly, roles);

  useEffect(() => {
    if (isOpen && personId) {
      fetchPersonDetails(personId);
    } else if (!isOpen) {
      // CRITICAL: Reset focus to prevent keyboard trap on Windows
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // Small delay to ensure focus is properly released
      setTimeout(() => {
        if (document.body) {
          document.body.focus();
        }
      }, 50);

      // Reset state when modal closes
      setActiveTab('overview');
      setPersonData(null);
      setError(null);
      setIsEditing(false);
      setSyncStatus({
        isSyncing: false,
        lastSyncAt: null,
        syncResult: null,
        syncMessage: '',
      });
    }
  }, [isOpen, personId]);

  // Set up the onPersonUpdate callback so parent components can trigger refresh
  useEffect(() => {
    if (isOpen && personId) {
      // Provide a refresh handler that parents can call
      const refreshHandler = (updatedPersonId: string) => {
        // Only refresh if it's this person's ID
        if (updatedPersonId === personId) {
          fetchPersonDetails(personId);
        }
      };

      setOnPersonUpdate(refreshHandler);

      // Cleanup when modal closes or personId changes
      return () => {
        setOnPersonUpdate(null);
      };
    } else {
      setOnPersonUpdate(null);
    }
  }, [isOpen, personId, setOnPersonUpdate]);

  // Built-in visitor action handlers (always available)
  const handleManualCheckIn = async (visitor: any) => {
    if (!personData?.customer?.id) return;

    setConfirmModal({
      isOpen: true,
      title: 'Registrar Entrada Manual',
      message: `Registrar entrada manual para ${visitor.person_name}? Isto marcará o visitante como "dentro".`,
      confirmText: 'Registrar Entrada',
      confirmButtonClass: 'bg-green-600 hover:bg-green-700',
      icon: 'entry',
      showDisclaimer: false,
      disclaimerType: 'entry',
      operatorName,
      requireAcknowledgment: false,
      isProcessing: false,
      onConfirm: async () => {
        try {
          // Set processing state
          setConfirmModal(prev => ({ ...prev, isProcessing: true }));

          // Fetch devices BEFORE check-in so zones are still populated
          const devicesForCheckIn = await fetchPersonDevices(personData.customer.id, visitor.person_id);

          const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;

          // In Electron: start local HYBRID removal first, then tell backend to skip cloud path
          if (isElectronApp) {
            revokeEntryDevices(personData.customer.id, visitor.person_id, visitor.person_name, devicesForCheckIn, 3).catch(() => {});
          }

          await condoApi.manualCheckIn(personData.customer.id, visitor.person_id, visitor.role_id, isElectronApp);
          toast.success('Entrada manual realizada com sucesso!');

          // In non-Electron: backend cloud path handles entry device removal
          if (!isElectronApp) {
            revokeEntryDevices(personData.customer.id, visitor.person_id, visitor.person_name, devicesForCheckIn, 3).catch(() => {});
          }

          // Refresh the person details
          if (personId) {
            await fetchPersonDetails(personId);
          }

          // Notify parent if callback exists
          if (onPersonUpdate) {
            onPersonUpdate(visitor.person_id);
          }

          // Close modal on success
          setConfirmModal(prev => ({ ...prev, isOpen: false, isProcessing: false }));
        } catch (error: any) {
          console.error('Failed to check-in visitor', error);
          toast.error(error.response?.data?.error || 'Erro ao registrar entrada');
          // Reset processing state on error
          setConfirmModal(prev => ({ ...prev, isProcessing: false }));
        }
      },
    });
  };

  const handleManualCheckOut = async (visitor: any) => {
    if (!personData?.customer?.id) return;

    setConfirmModal({
      isOpen: true,
      title: 'Registrar Saída',
      message: `Registrar saída de ${visitor.person_name}?`,
      confirmText: 'Registrar Saída',
      confirmButtonClass: 'bg-blue-600 hover:bg-blue-700',
      icon: 'exit',
      showDisclaimer: false,
      disclaimerType: 'general',
      operatorName,
      isProcessing: false,
      onConfirm: async () => {
        try {
          // Set processing state
          setConfirmModal(prev => ({ ...prev, isProcessing: true }));

          // Fetch devices BEFORE checkout — zones are empty after backend revokes access
          const devicesForCheckout = await fetchPersonDevices(personData.customer.id, visitor.person_id);

          const isElectronApp = typeof window !== 'undefined' && !!window.electronAPI;

          // In Electron: start local HYBRID removal first, then tell backend to skip cloud path
          if (isElectronApp) {
            removePersonFromDevices(personData.customer.id, devicesForCheckout, visitor.person_id, visitor.person_name, 3).catch(() => {});
          }

          // Check-out the visitor (backend skips cloud removal if Electron already handled it)
          await condoApi.manualCheckOut(personData.customer.id, visitor.person_id, visitor.role_id, isElectronApp);
          toast.success('Saída registrada com sucesso!');

          // Refresh the person details
          if (personId) {
            await fetchPersonDetails(personId);
          }

          // Notify parent if callback exists
          if (onPersonUpdate) {
            onPersonUpdate(visitor.person_id);
          }

          // Close modal on success
          setConfirmModal(prev => ({ ...prev, isOpen: false, isProcessing: false }));
        } catch (error: any) {
          console.error('Failed to check-out visitor', error);
          toast.error(error.response?.data?.error || 'Erro ao registrar saída');
          // Reset processing state on error
          setConfirmModal(prev => ({ ...prev, isProcessing: false }));
        }
      },
    });
  };

  const handleRevokeAccess = (visitor: any) => {
    if (!personData?.customer?.id) return;
    setRevokeModal({ isOpen: true, visitor });
  };

  const handleRevokeConfirm = async (reason: string) => {
    const { visitor } = revokeModal;
    if (!visitor || !personData?.customer?.id) return;
    try {
      // Fetch devices BEFORE revoke — zones are empty after backend revokes access
      const devicesForRevoke = await fetchPersonDevices(personData.customer.id, visitor.person_id);

      // The reason now flows into the revoke call itself (recorded in the tamper-
      // evident access ledger with the actor and timestamp) — the CustomerNote below
      // is kept as a human-readable duplicate for the existing notes UI.
      await condoApi.revokePersonRoleAuthorization(personData.customer.id, visitor.person_id, visitor.role_id, reason.trim() || undefined);

      if (reason.trim()) {
        await condoApi.createCustomerNote(
          personData.customer.id,
          `[Revogação] ${reason}`,
          { person_id: visitor.person_id }
        );
      }

      toast.success('Acesso revogado com sucesso!');

      // Remove from pre-fetched devices (fire-and-forget); failures persist to
      // localStorage and are retried automatically instead of being lost.
      revokeExitDevices(personData.customer.id, visitor.person_id, visitor.person_name, devicesForRevoke, 3).catch(err =>
        logger.error('person.revoke', 'Device removal failed', err)
      );

      // Refresh the person details
      if (personId) {
        await fetchPersonDetails(personId);
      }

      if (onPersonUpdate) {
        onPersonUpdate(visitor.person_id);
      }

      setRevokeModal({ isOpen: false, visitor: null });
    } catch (error: any) {
      console.error('Failed to revoke access', error);
      toast.error('Erro ao revogar acesso');
    }
  };

  // Handle reentry confirmation cancel
  const handleReentryCancel = () => {
    setShowReentryConfirmation(false);
    setReentryVisitorInfo(null);
  };

  // Handle reentry confirmation - finalize current visit and open registration modal
  const handleReentryConfirm = async () => {
    if (!reentryVisitorInfo) return;

    try {
      console.log(' Finalizing current visit before new entry:', reentryVisitorInfo.roleId);

      // Finalize the current visit by revoking the role
      if (personData?.customer?.id) {
        await condoApi.revokePersonRoleAuthorization(
          personData.customer.id,
          reentryVisitorInfo.personId,
          reentryVisitorInfo.roleId
        );

        toast.success('Visita anterior finalizada com sucesso!');

        // Refresh person details to get updated state
        if (personId) {
          await fetchPersonDetails(personId);
        }
      }

      // Close reentry modal
      setShowReentryConfirmation(false);
      setReentryVisitorInfo(null);

      // Open visitor registration modal for new visit
      setShowVisitorModal(true);
    } catch (error: any) {
      console.error('Failed to finalize visit:', error);
      toast.error('Erro ao finalizar visita anterior');
    }
  };

  const fetchPersonDetails = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/people/${id}/full?include_photos=true&include_roles=true`);
      setPersonData(response.data);
    } catch (err: any) {
      console.error('Failed to fetch person details:', err);
      if (err.response?.status === 404) {
        // Person no longer exists (deleted) — close modal silently
        closePersonDetail();
        return;
      }
      setError(err.response?.data?.error || 'Failed to load person details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch visitor stats when modal opens for visitors, service providers, or users without active role
  useEffect(() => {
    if (!isOpen || !personId || !personData) return;

    const fetchVisitorStats = async () => {
      try {
        const customerId = personData.customer?.id;
        if (!customerId) return;

        const stats = await condoApi.getPersonVisitorStats(customerId, personId);
        setVisitorStats(stats);
      } catch (error) {
        console.error('Failed to fetch visitor stats:', error);
        setVisitorStats(null);
      }
    };

    // Only fetch for visitors, service providers, or users without active role
    // CRITICAL: Check for ANY visitor role (active or inactive), not just active roles
    // This ensures we show stats for recently exited visitors
    const activeRole = personData.roles?.find(r => r.is_active);
    const hasVisitorRole = personData.roles?.some(r =>
      r.role_type === 'visitor' || r.role_type === 'service_provider'
    );

    if (
      hasVisitorRole ||
      !activeRole
    ) {
      fetchVisitorStats();
    }
  }, [isOpen, personId, personData]);

  // Helper function to check if Nova Visita button should be shown.
  // The rule considers ALL active roles — see ./personModalRules for why the previous
  // single-arbitrary-role version was order-dependent.
  const shouldShowNovaVisita = (): boolean => {
    if (!personData) return false;
    return shouldShowNovaVisitaRule(personData.roles);
  };

  const handleClose = () => {
    // Show confirmation if currently editing
    if (isEditing) {
      setShowCloseConfirm(true);
    } else {
      closePersonDetail();
    }
  };

  const handleForceClose = () => {
    setShowCloseConfirm(false);
    setIsEditing(false);
    closePersonDetail();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Sync person to all authorized devices
  /**
   * Opens the "how did they come in?" picker.
   *
   * It used to be a yes/no confirmation, so the event it wrote said "entrada" and nothing else —
   * the one thing nobody had to be told. What was missing is the part only the person at the desk
   * knows: on foot, in their own car, in an Uber whose plate they noted. See ManualAccessDialog.
   */
  const handleManualDirectionalClick = (direction: 'entrada' | 'saida') => {
    if (!personData?.customer?.id || !personData?.id) return;
    const activeRole = personData.roles?.find((r: any) => r.is_active && (r.role_type === 'resident' || r.role_type === 'staff' || r.role_type === 'condo_employee'));
    if (!activeRole) return;
    setManualAccess({ open: true, direction, roleId: activeRole.id, isProcessing: false });
  };

  const submitManualAccess = async (selection: ManualAccessSelection) => {
    if (!personData?.customer?.id || !personData?.id || !manualAccess.roleId) return;
    const { direction, roleId } = manualAccess;
    const isEntrada = direction === 'entrada';
    setManualAccess(prev => ({ ...prev, isProcessing: true }));
    try {
      const payload = manualAccessPayload(direction, selection);
      if (isEntrada) {
        await condoApi.registerManualEntry(personData.customer.id, personData.id, roleId, payload);
      } else {
        await condoApi.registerManualExit(personData.customer.id, personData.id, roleId, payload);
      }
      // The sentence, not a generic "registrado": it is what the operator just chose, and seeing it
      // echoed back is how a wrong pick gets noticed immediately rather than months later.
      toast.success(manualAccessDescription(direction, selection));
      if (onPersonUpdate) onPersonUpdate(personData.id);
      setManualAccess({ open: false, direction, roleId: null, isProcessing: false });
    } catch (error) {
      console.error(`Failed to register manual ${direction}`, error);
      toast.error(isEntrada ? 'Erro ao registrar entrada manual' : 'Erro ao registrar saída manual');
      setManualAccess(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const handleSyncPerson = async () => {
    if (!personData?.customer?.id || !personData?.id) return;

    const personName = `${personData.first_name} ${personData.last_name}`;

    setConfirmModal({
      isOpen: true,
      title: 'Sincronizar Dispositivos',
      message: `Sincronizar ${personName} com todos os dispositivos autorizados? Esta operação enviará as informações e permissões atualizadas para os dispositivos de controle de acesso.`,
      confirmText: 'Sincronizar',
      confirmButtonClass: 'bg-blue-600 hover:bg-blue-700',
      icon: 'warning',
      showDisclaimer: true,
      disclaimerType: 'general',
      operatorName,
      isProcessing: false,
      onConfirm: async () => {
        // Show processing state in modal
        setConfirmModal(prev => ({ ...prev, isProcessing: true }));
        setSyncStatus(prev => ({ ...prev, isSyncing: true, syncResult: null, syncMessage: '' }));

        // Close modal after brief delay (runs sync in background)
        setTimeout(() => {
          setConfirmModal(prev => ({ ...prev, isOpen: false, isProcessing: false }));
        }, 800);

        try {
          const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

          // Try hybrid direct communication in Electron
          if (isElectron) {
            try {
              const allDeviceDetails = await fetchPersonDeviceHandles(
                personData.customer.id,
                personData.id,
              );

              if (allDeviceDetails.length > 0) {
                const deviceDetails = allDeviceDetails;
                const activeVisitorRole = personData.roles?.find((r: any) =>
                  (r.role_type === 'visitor' || r.role_type === 'service_provider') &&
                  r.is_active && r.is_authorized
                );
                const isVisitorInside = activeVisitorRole?.role_metadata?.visitor_state === 'inside';

                // Skip entrada devices when visitor is already inside
                const validDevices = deviceDetails
                  .filter(d => !(isVisitorInside && d.direction === 'entrada'));

                if (validDevices.length > 0) {
                  // Pre-fetch photo with Bearer token.
                  // fetchImageAsBase64 (inside uploadFacePhoto) uses plain fetch() with no auth,
                  // causing silent 401s for backend image URLs. Passing a data URI skips that fetch.
                  const rawPhotoUrl = personData.identity_photo_url || personData.profile_picture_url;
                  let photoDataUri: string | undefined;
                  if (rawPhotoUrl) {
                    try {
                      const fullPhotoUrl = rawPhotoUrl.startsWith('/')
                        ? `https://access.panther.com.br${rawPhotoUrl}`
                        : rawPhotoUrl;
                      const photoResp = await fetchWithAuth(fullPhotoUrl);
                      if (photoResp.ok) {
                        const photoBlob = await photoResp.blob();
                        photoDataUri = await new Promise<string>((res, rej) => {
                          const r = new FileReader();
                          r.onloadend = () => res(r.result as string);
                          r.onerror = rej;
                          r.readAsDataURL(photoBlob);
                        });
                        console.log(` [ModalSync] Pre-fetched photo: ${photoDataUri.length} chars`);
                      } else {
                        console.warn(` [ModalSync] Photo fetch HTTP ${photoResp.status}`);
                      }
                    } catch (photoErr: any) {
                      console.warn(' [ModalSync] Photo pre-fetch failed:', photoErr?.message);
                    }
                  }

                  // Detect visitor role to pass visitor timing to devices
                  const visitorRole = personData.roles?.find(r =>
                    (r.role_type === 'visitor' || r.role_type === 'service_provider') &&
                    r.is_active && r.is_authorized
                  );
                  const isVisitor = !!visitorRole;
                  // Windows come from the ONE canonical implementation (fully unit-tested).
                  // This block previously inlined its own copy, which had drifted from
                  // resolveDeviceSyncTimes on the end-of-day fallback (local vs UTC).
                  const roleTiming = {
                    valid_from: visitorRole?.valid_from,
                    valid_until: visitorRole?.valid_until,
                    role_metadata: {
                      entry_window_expires_at: visitorRole?.role_metadata?.entry_window_expires_at,
                      access_expires_at: visitorRole?.role_metadata?.access_expires_at,
                    },
                  };
                  const visitorValidFrom = resolveDeviceSyncTimes(roleTiming, null).validFrom;
                  const visitorValidUntil = resolveDeviceSyncTimes(roleTiming, null).validUntil;
                  const visitorEntryWindowUntil = resolveDeviceSyncTimes(roleTiming, 'entrada').validUntil;

                  // Fetch fingerprint templates so they are pushed to fingerprint-capable devices
                  let fpTemplates: Array<{ templateB64: string }> = [];
                  try {
                    const fpRes = await api.get(`/customers/${personData.customer.id}/people/${personData.id}/fingerprints`);
                    const fps: any[] = fpRes.data?.fingerprints || fpRes.data || [];
                    fpTemplates = fps
                      .filter((fp: any) => !!fp.template_b64)
                      .map((fp: any) => ({ templateB64: fp.template_b64 }));
                  } catch (fpErr: any) {
                    console.warn('[ModalSync] Could not fetch fingerprints (continuing without):', fpErr?.message);
                  }

                  // One builder for every full person sync — see domain/personSyncData.
                  // This payload carried fingerprints but NOT the person's vehicle tags, so
                  // syncing from here left the tag off the reader (and, worse, a photo
                  // recovery that recreates the device user could drop a card it never knew
                  // about). PersonProfile had the mirror-image gap.
                  const personSyncData = buildPersonSyncPayload(personData as any, fpTemplates, {
                    photoDataUri,
                    isVisitor,
                    validFrom: visitorValidFrom,
                    validUntil: visitorValidUntil,
                  });

                  const toastId = `modal-sync-${personData.id}`;
                  const total = validDevices.length;
                  const successfulDevices: { device_id: string; device_name: string; source?: string }[] = [];
                  const failedDevices: { device_id: string; device_name: string; error: string }[] = [];

                  // One surface for every sync (see SyncProgressProvider). `controls: false`
                  // because this path fans every device out at once and cannot honour pause/stop.
                  const reporter = makeSyncReporter({
                    subject: {
                      id: personData.id,
                      name: personName,
                      photoUrl: personData.identity_photo_url || personData.profile_picture_url || undefined,
                    },
                    total,
                    controls: false,
                  });
                  reporter.start();

                  await Promise.all(validDevices.map(async (deviceInfo) => {
                    // Per-device validUntil: entrada uses entry window (10-15min), saida/passagem use access window (14h)
                    const deviceSyncData = (isVisitor && deviceInfo.direction === 'entrada')
                      ? { ...personSyncData, validUntil: visitorEntryWindowUntil }
                      : personSyncData;

                    // Executes, records the outcome in BOTH the audit trail and the
                    // reconciliation ledger, and classifies. See utils/syncDeviceAndReport.
                    const outcome = await syncDeviceAndReport({
                      customerId: personData.customer.id,
                      personId: personData.id,
                      device: deviceInfo,
                      payload: deviceSyncData,
                    });

                    if (outcome.failed) {
                      failedDevices.push(outcome.failed);
                      reporter.record(outcome.failed.device_name, 'failed', outcome.failed.error);
                      return;
                    }

                    successfulDevices.push(outcome.synced);
                    reporter.record(outcome.synced.device_name, 'ok');

                    // ── ControlID post-sync ──
                    const source = outcome.synced.source;
                    if (deviceInfo.vendor === 'controlid' && (source === 'local' || source === 'public')) {
                      try {
                        const groupId = await DeviceIntegration.instance.ensureControlIDDefaultAccess(deviceInfo);
                        if (groupId !== null) {
                          const { assigned, skipped } = await DeviceIntegration.instance.assignAllDeviceUsersToGroup(deviceInfo, groupId);
                          console.log(`[ModalSync]  ControlID bulk group: device=${deviceInfo.id}, assigned=${assigned}, skipped=${skipped}`);
                        }
                      } catch (groupErr: any) {
                        console.warn(`[ModalSync] Group assign failed for device ${deviceInfo.id}:`, groupErr?.message);
                      }
                    }
                  }));

                  // Close the progress presentation, then show final results as fresh toasts
                  reporter.finish();

                  // One toast per distinct CAUSE (not per device) — four devices rejecting the
                  // same photo used to produce four identical walls of vendor JSON.
                  for (const group of groupDeviceFailures(failedDevices)) {
                    console.warn('[ModalSync] device failures:', group.deviceNames, group.message);
                    toast.error(deviceFailureToast(group), { duration: 7000 });
                  }

                  if (successfulDevices.length > 0) {
                    const localCount = successfulDevices.filter(d => d.source === 'local').length;
                    const publicCount = successfulDevices.filter(d => d.source === 'public').length;
                    const cloudCount = successfulDevices.filter(d => d.source === 'cloud').length;
                    const modeBreakdown: string[] = [];
                    if (localCount > 0) modeBreakdown.push(`${localCount} local`);
                    if (publicCount > 0) modeBreakdown.push(`${publicCount} público`);
                    if (cloudCount > 0) modeBreakdown.push(`${cloudCount} nuvem`);

                    const syncMessage = formatSyncSuccess(
                      successfulDevices.map(d => d.device_name),
                      modeBreakdown,
                      successfulDevices.length + failedDevices.length,
                    );
                    // No summary toast: the pinned card already shows this run's verdict
                    // and counts. Repeating it beside the card is the same sync reported
                    // twice, with two different lifetimes.

                    setSyncStatus({
                      isSyncing: false,
                      lastSyncAt: new Date(),
                      syncResult: failedDevices.length > 0 ? 'partial' : 'success',
                      syncMessage,
                      successfulDevices,
                      failedDevices,
                    });
                    return;
                  }

                  // All local attempts failed.
                  // ARCHITECTURE RULE: inside Electron, device sync is executed LOCALLY only —
                  // never delegated to the cloud. A local failure is surfaced as a failure.
                  setSyncStatus({
                    isSyncing: false,
                    lastSyncAt: new Date(),
                    syncResult: 'error',
                    syncMessage: `Falha ao sincronizar ${failedDevices.length} dispositivo(s) localmente`,
                    successfulDevices: [],
                    failedDevices,
                  });
                  return;
                }
              }
              // No devices resolved for local sync — do not delegate to the cloud in Electron.
              toast.error('Nenhum dispositivo encontrado para sincronização local');
              setSyncStatus(prev => ({
                ...prev,
                isSyncing: false,
                syncResult: 'error',
                syncMessage: 'Nenhum dispositivo encontrado para sincronização local',
              }));
              return;
            } catch (hybridErr: any) {
              // ARCHITECTURE RULE: never fall back to cloud sync inside Electron.
              console.error('[ModalSync] Local sync failed:', hybridErr);
              toast.error('Erro ao sincronizar dispositivos localmente');
              setSyncStatus(prev => ({
                ...prev,
                isSyncing: false,
                syncResult: 'error',
                syncMessage: 'Erro ao sincronizar dispositivos localmente',
              }));
              return;
            }
          }

          // Web browser (non-Electron): the backend executes the sync via cloud.
          const response = await api.post(`/customers/${personData.customer.id}/people/${personData.id}/sync`);

          const result = response.data;
          const successfulDevices = result.successful_devices || [];
          const failedDevices = result.failed_devices || [];

          let syncResult: 'success' | 'error' | 'partial' = 'success';
          let syncMessage = '';

          if (failedDevices.length > 0 && successfulDevices.length === 0) {
            syncResult = 'error';
            syncMessage = `Falha ao sincronizar ${failedDevices.length} dispositivo(s)`;
            toast.error(syncMessage, { duration: 5000 });
          } else if (failedDevices.length > 0) {
            syncResult = 'partial';
            syncMessage = `${successfulDevices.length} sincronizado(s), ${failedDevices.length} com erro`;
            toast.success(`${successfulDevices.length} dispositivo(s) sincronizado(s)`, { duration: 4000 });
            toast.error(`${failedDevices.length} falharam`, { duration: 4000 });
          } else {
            syncResult = 'success';
            syncMessage = `${successfulDevices.length} dispositivo(s) sincronizado(s)`;
            toast.success(syncMessage, { duration: 4000 });
          }

          setSyncStatus({
            isSyncing: false,
            lastSyncAt: new Date(),
            syncResult,
            syncMessage,
            successfulDevices,
            failedDevices,
          });

        } catch (error: any) {
          console.error('Failed to sync person:', error);
          toast.error(error.response?.data?.error || 'Erro ao sincronizar');
          setSyncStatus(prev => ({
            ...prev,
            isSyncing: false,
            syncResult: 'error',
            syncMessage: 'Erro ao sincronizar',
          }));
        }
      },
    });
  };

  // Remove person from all enrolled devices
  const handleRevokeDevices = () => {
    if (!personData?.customer?.id || !personData?.id) return;
    const personName = `${personData.first_name} ${personData.last_name}`;

    setConfirmModal({
      isOpen: true,
      title: 'Revogar Dispositivos',
      message: `Remover ${personName} de todos os dispositivos? Esta operação remove o acesso físico imediatamente, mas não altera as permissões cadastradas.`,
      confirmText: 'Revogar',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      icon: 'revoke',
      showDisclaimer: false,
      disclaimerType: 'general',
      operatorName,
      isProcessing: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isProcessing: true }));
        try {
          const devices = await fetchPersonDevices(personData.customer.id, personData.id);
          await removePersonFromDevices(personData.customer.id, devices, personData.id, personName, 3);
          toast.success(`${personName} removido(a) dos dispositivos`);
          setConfirmModal(prev => ({ ...prev, isOpen: false, isProcessing: false }));
        } catch (error: any) {
          console.error('Failed to revoke devices:', error);
          toast.error('Erro ao revogar dispositivos');
          setConfirmModal(prev => ({ ...prev, isProcessing: false }));
        }
      },
    });
  };

  // Navigate to full profile page
  const handleVisitProfile = () => {
    if (personData?.id) {
      closePersonDetail(); // Close the modal first
      // If there's a custom handler (e.g., from operational view), use it
      // Otherwise, use React Router navigation
      if (onVisitProfile) {
        onVisitProfile(personData.id);
      } else {
        navigate(`/people/${personData.id}`);
      }
    }
  };

  // Build photo gallery from person data
  // Open photo viewer
  const openPhotoViewer = (photoUrl?: string) => {
    const { gallery, startIndex } = buildPhotoGallery(personData, photoUrl);
    if (gallery.length === 0) return;

    setPhotoGallery(gallery);
    setCurrentPhotoIndex(startIndex);
    setPhotoViewerOpen(true);
  };

  // Close photo viewer
  const closePhotoViewer = () => {
    setPhotoViewerOpen(false);
  };

  // Navigate to previous photo
  const goToPreviousPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : photoGallery.length - 1));
  };

  // Navigate to next photo
  const goToNextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev < photoGallery.length - 1 ? prev + 1 : 0));
  };

  // Every route to replacing a photo — the lightbox, the sidebar thumbnails, the Documentos
  // tab — goes through the one flow mounted below, so none of them can drift into its own.
  const openPhotoEdit = (item: PhotoGalleryItem) => {
    if (!item.photoId) return;
    // Re-crop from the untouched upload, not from the crop already in place — cropping a
    // crop is how a face gets cut off a second time, and is the whole reason the original
    // is stored. Falls back to the crop for rows that predate migration 230.
    setPhotoViewerOpen(false);
    photoFlowRef.current?.update({
      photoId: item.photoId,
      url: item.originalUrl || item.url,
      photoType: item.photoType,
    });
  };

  // Reset loading state whenever the displayed photo changes
  useEffect(() => { setLightboxPhotoLoaded(false); setLightboxOriginalFailed(false); }, [currentPhotoIndex]);
  // Reset sidebar photo loader whenever a different person is opened
  useEffect(() => { setSidebarPhotoLoaded(false); }, [personId]);

  // Keyboard navigation for modal and photo viewer
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      // Photo viewer is open - handle its keyboard events first
      if (photoViewerOpen) {
        if (e.key === 'Escape') {
          closePhotoViewer();
        } else if (e.key === 'ArrowLeft') {
          goToPreviousPhoto();
        } else if (e.key === 'ArrowRight') {
          goToNextPhoto();
        }
      }
      // Modal is open but photo viewer is not
      else if (isOpen && e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeydown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, photoViewerOpen]);

  // Helper function to show confirmation modal for PermissionsTab
  const showConfirmModalForPermissions = (config: {
    title: string;
    message: string;
    confirmText: string;
    confirmButtonClass: string;
    icon: 'warning' | 'entry' | 'exit' | 'revoke';
    showDisclaimer?: boolean;
    disclaimerType?: 'entry' | 'revoke' | 'general';
    onConfirm: () => Promise<void> | void;
  }) => {
    setConfirmModal({
      isOpen: true,
      title: config.title,
      message: config.message,
      confirmText: config.confirmText,
      confirmButtonClass: config.confirmButtonClass,
      icon: config.icon,
      showDisclaimer: config.showDisclaimer,
      disclaimerType: config.disclaimerType,
      operatorName,
      isProcessing: false,
      onConfirm: async () => {
        // Close modal immediately and let user continue working
        setConfirmModal(prev => ({ ...prev, isOpen: false, isProcessing: false }));

        // Execute the async operation in background
        try {
          await config.onConfirm();
        } catch (error) {
          // Error handling is done in the onConfirm function itself
        }
      },
    });
  };

  if (!isOpen) return null;

  // ASO tab visibility: enabled for customer + person's role_type matches applies_to list.
  // See ./personModalRules for the role_type -> category mapping.
  const showASOTab = shouldShowASOTab(personData?.customer, personData?.roles);

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Visão Geral', icon: <User className="w-4 h-4" /> },
    { id: 'permissions', label: 'Permissões', icon: <Shield className="w-4 h-4" /> },
    { id: 'history', label: 'Histórico', icon: <Clock className="w-4 h-4" /> },
    { id: 'photos', label: 'Fotos', icon: <Camera className="w-4 h-4" /> },
    { id: 'documents', label: 'Documentos', icon: <FileText className="w-4 h-4" /> },
    { id: 'vehicles', label: 'Veículos', icon: <Car className="w-4 h-4" /> },
    { id: 'tags', label: 'Tags', icon: <Tag className="w-4 h-4" /> },
    { id: 'notes', label: 'Notas', icon: <MessageSquare className="w-4 h-4" /> },
    ...(showASOTab ? [{ id: 'aso' as const, label: 'ASO', icon: <HeartPulse className="w-4 h-4" /> }] : []),
    { id: 'biometria' as const, label: 'Biometria', icon: <Fingerprint className="w-4 h-4" /> },
  ];

  const currentPhoto = photoGallery[currentPhotoIndex];
  // Prefer the original; the crop is both the fallback and what we drop back to if the
  // original cannot be fetched.
  const lightboxShownUrl = (!lightboxOriginalFailed && currentPhoto?.originalUrl) || currentPhoto?.url;

  // Get active role info
  const activeRole = personData?.roles?.find(r => r.is_active);
  const customerType = personData?.customer?.type;
  const condoType = personData?.customer?.condo_data?.condo_type;

  return (
    <>
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
        onClick={handleBackdropClick}
      >
        <div
          className={cn(
            "relative w-full max-w-4xl max-h-[90vh] rounded-xl shadow-2xl",
            "transform transition-all duration-300 ease-out",
            "flex flex-col",
            isOpen ? "scale-100 opacity-100" : "scale-95 opacity-0",
            darkMode ? "bg-gray-900" : "bg-white"
          )}
        >
          {/* Header */}
          <div className={cn(
            "flex-shrink-0 px-5 py-4 border-b",
            darkMode ? "border-gray-800" : "border-gray-200"
          )}>
            <div className="relative">
              {/* Top-right buttons */}
              <div className="absolute top-0 right-0 flex items-center gap-1">
                {personData && (
                  <button
                    onClick={handleVisitProfile}
                    title="Visitar Perfil"
                    className={cn(
                      "p-1.5 text-gray-400 rounded-lg transition-colors",
                      darkMode
                        ? "hover:text-gray-300 hover:bg-gray-800"
                        : "hover:text-gray-600 hover:bg-gray-100"
                    )}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={handleClose}
                  aria-label="Fechar"
                  title="Fechar"
                  className={cn(
                    "p-1.5 text-gray-400 rounded-lg transition-colors",
                    darkMode
                      ? "hover:text-gray-300 hover:bg-gray-800"
                      : "hover:text-gray-600 hover:bg-gray-100"
                  )}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 min-w-0">
                {/* Title Row: Person Name + Customer + User ID */}
                <div className="flex items-center gap-2 flex-wrap pr-16">
                  {loading ? (
                    <div className={cn("h-6 w-48 rounded animate-pulse", darkMode ? "bg-gray-700" : "bg-gray-200")} />
                  ) : (
                    <h2 className={cn(
                      "text-lg font-semibold",
                      darkMode ? "text-gray-100" : "text-gray-900"
                    )}>
                      {personData ? `${personData.first_name} ${personData.last_name}` : 'Detalhes da Pessoa'}
                    </h2>
                  )}

                  {loading ? (
                    <div className={cn("h-4 w-24 rounded animate-pulse", darkMode ? "bg-gray-700" : "bg-gray-200")} />
                  ) : personData?.customer?.name && (
                    <>
                      <span className={cn("text-sm", darkMode ? "text-gray-600" : "text-gray-300")}>·</span>
                      <span className={cn("text-sm font-medium", darkMode ? "text-gray-400" : "text-gray-500")}>
                        {personData.customer.name}
                      </span>
                    </>
                  )}

                </div>

                {/* Info strip — visitor/prestador only */}
                {personData && personData.roles?.some((r: any) => r.role_type === 'visitor' || r.role_type === 'service_provider') && visitorStats && (() => {
                  const visitorRole = personData.roles?.find((r: any) =>
                    (r.role_type === 'visitor' || r.role_type === 'service_provider') && r.is_active
                  );
                  const authorizedBy = visitorRole?.role_metadata?.authorized_by_name || personData.created_by_name;
                  const activeVisitorRole = personData.roles?.find((r: any) =>
                    (r.role_type === 'visitor' || r.role_type === 'service_provider') &&
                    r.is_active === true && r.role_metadata?.visitor_state
                  );
                  const visitorState = activeVisitorRole?.role_metadata?.visitor_state;
                  const currentVisitor = activeVisitorRole ? {
                    person_id: personData.id,
                    role_id: activeVisitorRole.id,
                    person_name: `${personData.first_name} ${personData.last_name}`,
                    person_type: activeVisitorRole.role_type,
                    visitor_state: visitorState,
                  } : null;
                  const checkInHandler = visitorActionHandlers?.onManualCheckIn || handleManualCheckIn;
                  const checkOutHandler = visitorActionHandlers?.onManualCheckOut || handleManualCheckOut;
                  const revokeHandler = visitorActionHandlers?.onRevokeAccess || handleRevokeAccess;

                  const sep = <span className={cn("self-stretch w-px mx-1", darkMode ? "bg-gray-700" : "bg-gray-200")} />;
                  const field = (label: string, value: React.ReactNode) => (
                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                      <span className={cn("text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap", darkMode ? "text-gray-500" : "text-gray-400")}>{label}</span>
                      <div className={cn("text-xs font-medium whitespace-nowrap", darkMode ? "text-gray-200" : "text-gray-700")}>{value}</div>
                    </div>
                  );

                  return (
                    <div className={cn(
                      "mt-3 rounded-lg border px-2.5 py-2 flex flex-col gap-1.5",
                      darkMode ? "bg-gray-800/40 border-gray-700/50" : "bg-gray-50 border-gray-200"
                    )}>
                      {/* Row 1 — info fields */}
                      <div className="flex items-center gap-3 min-w-0">
                        {/* Estado */}
                        {field('Estado',
                          visitorState === 'pre_registered'
                            ? <span className="inline-flex items-center gap-1 font-semibold text-yellow-600 whitespace-nowrap"><span className="w-1.5 h-1.5 bg-yellow-500 rounded-full flex-shrink-0" />Pré-Cadastrado</span>
                            : visitorStats.current_status === 'active'
                              ? <span className="inline-flex items-center gap-1 font-semibold text-green-600 whitespace-nowrap"><span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse flex-shrink-0" />Visita Ativa</span>
                              : <span className={cn("whitespace-nowrap", darkMode ? "text-gray-400" : "text-gray-500")}>Sem visita</span>
                        )}

                        {visitorStats.active_visit && (
                          <>{sep}{field('Entrada', <span className="whitespace-nowrap">{new Date(visitorStats.active_visit.check_in_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>)}</>
                        )}

                        {/* Local */}
                        {visitorStats.active_visit?.unit_number && (() => {
                          const locationId = visitorRole?.role_metadata?.unit_id || visitorRole?.role_metadata?.unit_info?.id;
                          const locationLabel = (visitorRole?.role_metadata?.unit_display as string | undefined) ||
                            [visitorStats.active_visit!.block_name, visitorStats.active_visit!.unit_number].filter(Boolean).join(' ');
                          const isEmpresa = customerType === 'empresa';
                          const canClick = !!(locationId && (isEmpresa ? onDepartmentClick : onUnitClick));
                          const handleLocalClick = () => {
                            closePersonDetail();
                            if (isEmpresa) onDepartmentClick!(locationId!, locationLabel);
                            else onUnitClick!(locationId!, locationLabel);
                          };
                          return (
                            <>{sep}
                              <div className="flex flex-col gap-0.5 min-w-0 max-w-[130px]">
                                <span className={cn("text-[10px] uppercase tracking-wider font-semibold", darkMode ? "text-gray-500" : "text-gray-400")}>Local</span>
                                {canClick ? (
                                  <button
                                    onClick={handleLocalClick}
                                    className={cn("text-xs font-medium truncate text-left transition-colors", darkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800")}
                                    title={`Ver ${isEmpresa ? 'departamento' : 'unidade'} ${locationLabel}`}
                                  >
                                    {locationLabel}
                                  </button>
                                ) : (
                                  <span className={cn("text-xs font-medium truncate", darkMode ? "text-gray-200" : "text-gray-700")} title={locationLabel}>{locationLabel}</span>
                                )}
                              </div>
                            </>
                          );
                        })()}

                        {/* Autorizado por */}
                        {authorizedBy && (() => {
                          const authorizedById = visitorRole?.role_metadata?.authorized_by_resident_id || visitorRole?.role_metadata?.authorized_by_person_id;
                          const canClick = !!authorizedById;
                          return (
                            <>{sep}
                              <div className="flex flex-col gap-0.5 min-w-0 max-w-[140px]">
                                <span className={cn("text-[10px] uppercase tracking-wider font-semibold", darkMode ? "text-gray-500" : "text-gray-400")}>Autorizado por</span>
                                {canClick ? (
                                  <button
                                    onClick={() => openPersonDetail(authorizedById!, false, darkMode)}
                                    className={cn("text-xs font-medium truncate text-left transition-colors", darkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800")}
                                    title={`Ver perfil de ${authorizedBy}`}
                                  >
                                    {authorizedBy}
                                  </button>
                                ) : (
                                  <span className={cn("text-xs font-medium truncate", darkMode ? "text-gray-200" : "text-gray-700")} title={authorizedBy}>{authorizedBy}</span>
                                )}
                              </div>
                            </>
                          );
                        })()}

                        {visitorStats.active_visit && (visitorStats.active_visit.access_expires_at || visitorStats.active_visit.valid_until) && (
                          <>{sep}{field('Acesso', <CountdownTimer expiresAt={(visitorStats.active_visit.access_expires_at || visitorStats.active_visit.valid_until)!} />)}</>
                        )}
                      </div>

                      {/* Row 2 — action buttons (right-aligned) */}
                      {/* Pre-registered visitors have no active visit — hide all action buttons */}
                      {currentVisitor && visitorState !== 'pre_registered' && (
                        <div className="flex items-center justify-end gap-1.5">
                          {visitorState === 'awaiting_entry' && (
                            <button onClick={() => checkInHandler(currentVisitor)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium transition-colors bg-green-500/15 hover:bg-green-500/25 text-green-600 whitespace-nowrap">
                              <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" /></svg>
                              Entrada Manual
                            </button>
                          )}
                          {visitorState === 'inside' && (
                            <button onClick={() => checkOutHandler(currentVisitor)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium transition-colors bg-blue-500/15 hover:bg-blue-500/25 text-blue-600 whitespace-nowrap">
                              <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" /></svg>
                              Saída
                            </button>
                          )}
                          <button onClick={() => revokeHandler(currentVisitor)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium transition-colors bg-red-500/15 hover:bg-red-500/25 text-red-600 whitespace-nowrap">
                            <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" /></svg>
                            Revogar Acesso
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>
            </div>
          </div>

          {/* Body - Two Column Layout */}
          <div className="flex-1 flex overflow-hidden">
            <div className="flex flex-1 min-h-0">
              {/* Left Sidebar - Photo, Nav & Actions */}
              <div className={cn("w-52 flex-shrink-0 flex flex-col border-r", darkMode ? "border-gray-800" : "border-gray-100")}>
                {/* Scrollable top: photo + nav tabs */}
                <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
                <div className="flex flex-col items-center px-4 pt-5 pb-3">
                  {/* Photo Container */}
                  <div className="relative">
                    {(() => {
                      // Prefer cached photo (already downloaded for visitor list) to avoid re-download.
                      // The API returns a fresh presigned S3 URL every time, which the browser treats
                      // as a different resource. Using the cached URL means zero extra network requests.
                      const cachedPhoto = personId ? getPersonPhoto(personId) ?? null : null;
                      const photoUrl = cachedPhoto || (personData ? getPrimaryPhotoUrl(personData) : null);
                      return photoUrl ? (
                        <button
                          onClick={() => personData && openPhotoViewer(photoUrl)}
                          className="relative group"
                          aria-label="Ver foto em tela cheia"
                        >
                          {!sidebarPhotoLoaded && (
                            <div className={cn(
                              "absolute inset-0 rounded-xl flex items-center justify-center border-4",
                              darkMode ? "bg-gray-800 border-gray-700" : "bg-gray-100 border-gray-200"
                            )}>
                              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                            </div>
                          )}
                          <img
                            src={photoUrl}
                            alt={personData ? `${personData.first_name} ${personData.last_name}` : ''}
                            className={cn(
                              "w-32 h-40 rounded-xl object-cover border-4 transition-transform group-hover:scale-105",
                              darkMode ? "border-gray-700" : "border-gray-200",
                              !sidebarPhotoLoaded && "opacity-0"
                            )}
                            onLoad={() => setSidebarPhotoLoaded(true)}
                            onError={(e) => {
                              setSidebarPhotoLoaded(true);
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                          {/* Hover overlay */}
                          <div className="absolute inset-0 rounded-xl bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                        </button>
                      ) : (
                      <button
                        onClick={() => {
                          if (!effectiveReadOnly && personData) {
                            setActiveTab('photos');
                            setTriggerPhotoAdd(true);
                          }
                        }}
                        className={cn(
                          "relative group w-32 h-40 rounded-xl border-4 flex items-center justify-center transition-colors",
                          darkMode
                            ? "bg-gray-800 border-gray-700"
                            : "bg-gray-100 border-gray-200",
                          !effectiveReadOnly && personData && "cursor-pointer"
                        )}
                        disabled={effectiveReadOnly || !personData}
                      >
                        {loading ? (
                          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                        ) : (
                          <span className="text-3xl font-semibold text-gray-400 group-hover:opacity-0 transition-opacity">
                            {personData ? `${personData.first_name[0]}${personData.last_name[0]}` : '?'}
                          </span>
                        )}
                        {!effectiveReadOnly && personData && (
                          <div className="absolute inset-0 rounded-xl flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 gap-1">
                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="text-white text-[10px] font-semibold">Adicionar foto</span>
                          </div>
                        )}
                      </button>
                      );
                    })()}
                  </div>

                  {/* Document + Vehicle photo thumbnails */}
                  {!loading && personData && (() => {
                    const docPhotos = personData.identity_photos?.filter(p => p.photo_type?.startsWith('document')) || [];
                    const docFrontUrl = getAbsoluteImageUrl(docPhotos.find(p => p.photo_type === 'document_front')?.photo_url || personData.document_front_url);
                    const docBackUrl  = getAbsoluteImageUrl(docPhotos.find(p => p.photo_type === 'document_back')?.photo_url  || personData.document_back_url);
                    const vehicleWithPhoto = personData.vehicles?.find(v => v.is_active && v.photo_url);
                    const vehiclePhotoFromIdentity = personData.identity_photos?.find(p => p.photo_type === 'vehicle')?.photo_url;
                    const vehiclePhotoUrl = getAbsoluteImageUrl(vehicleWithPhoto?.photo_url || vehiclePhotoFromIdentity);
                    const slots = [
                      { url: docFrontUrl,     line1: 'Documento', line2: '(Frente)', Icon: FileText, photoType: 'document_front' },
                      { url: docBackUrl,      line1: 'Documento', line2: '(Verso)',  Icon: FileText, photoType: 'document_back'  },
                      { url: vehiclePhotoUrl, line1: 'Veículo',   line2: '',         Icon: Car,      photoType: 'vehicle'         },
                    ];
                    return (
                      <div className="w-full mt-3 grid grid-cols-3 gap-1 px-1">
                        {slots.map(({ url, line1, line2, Icon, photoType }) => (
                          <button
                            key={line1 + line2}
                            onClick={() => {
                              if (url) {
                                openPhotoViewer(url);
                              } else if (!effectiveReadOnly) {
                                photoFlowRef.current?.create({ photoType });
                              }
                            }}
                            className="group flex flex-col items-center gap-0.5"
                          >
                            <div className={cn(
                              "w-full aspect-[4/3] rounded border relative overflow-hidden flex items-center justify-center",
                              darkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-100"
                            )}>
                              {url ? (
                                <>
                                  <img
                                    src={url}
                                    alt={line1}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                  />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                                </>
                              ) : (
                                <Icon className={cn("w-4 h-4", darkMode ? "text-gray-600" : "text-gray-400")} strokeWidth={1.5} />
                              )}
                            </div>
                            <div className={cn("h-[22px] flex flex-col items-center justify-start leading-[1.2]", darkMode ? "text-gray-500" : "text-gray-400")}>
                              <span className="text-[9px]">{line1}</span>
                              <span className="text-[9px]">{line2}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Status + Type badges below photo */}
                  {loading && (
                    <div className="flex items-center gap-1.5 mt-3 justify-center">
                      <div className={cn("h-5 w-12 rounded-full animate-pulse", darkMode ? "bg-gray-700" : "bg-gray-200")} />
                      <div className={cn("h-5 w-16 rounded-full animate-pulse", darkMode ? "bg-gray-700" : "bg-gray-200")} />
                    </div>
                  )}
                  {!loading && personData && (
                    <div className="flex flex-col items-center gap-1.5 mt-3 w-full px-2">
                      <div className="flex items-center gap-1.5 flex-wrap justify-center">
                        <span className={cn(
                          "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium",
                          personData.is_active
                            ? darkMode ? "bg-green-900/30 text-green-400" : "bg-green-100 text-green-700"
                            : darkMode ? "bg-red-900/30 text-red-400" : "bg-red-100 text-red-700"
                        )}>
                          <span className={cn("w-1.5 h-1.5 rounded-full", personData.is_active ? "bg-green-500" : "bg-red-500")} />
                          {personData.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                        {personData.roles && personData.roles.length > 0 && (() => {
                          const uniqueRoleTypes = Array.from(new Set(personData.roles.map((r: any) => r.role_type)));
                          return uniqueRoleTypes.map((roleType: any) => (
                            <span key={roleType} className={cn(
                              "px-2 py-0.5 rounded-full text-xs font-medium",
                              darkMode ? "bg-blue-900/30 text-blue-400" : "bg-blue-100 text-blue-700"
                            )}>
                              {translatePersonTypeToPortuguese(roleType)}
                            </span>
                          ));
                        })()}
                      </div>

                      {/* Nova Visita button */}
                      {shouldShowNovaVisita() && (() => {
                        const visitorStatus = getVisitorStatus(personData);
                        const isActiveNow = visitorStatus.isActive && (visitorStatus.status === 'inside' || visitorStatus.status === 'awaiting');
                        const handleNovaVisitaClick = () => {
                          const activeVisit = personData.roles?.find((role: any) =>
                            (role.role_type === 'visitor' || role.role_type === 'service_provider') &&
                            role.role_metadata?.visitor_state === 'inside'
                          );
                          if (activeVisit) {
                            setReentryVisitorInfo({
                              personId: personData.id,
                              roleId: activeVisit.id,
                              personName: `${personData.first_name} ${personData.last_name}`,
                              unit: activeVisit.role_metadata?.unit_display || activeVisit.role_metadata?.unit || 'Não especificada',
                              entryTime: activeVisit.role_metadata?.last_entry_at || '',
                              personData
                            });
                            setShowReentryConfirmation(true);
                          } else {
                            setShowVisitorModal(true);
                          }
                        };
                        return (
                          <button onClick={handleNovaVisitaClick}
                            className={cn("w-full mt-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors",
                              isActiveNow
                                ? darkMode ? "bg-amber-900/30 text-amber-400 hover:bg-amber-900/50" : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                                : "bg-blue-600 hover:bg-blue-700 text-white"
                            )}>
                            <UserPlus className="h-3 w-3" />
                            Nova Visita
                          </button>
                        );
                      })()}

                      {/* Visitas count — only for visitors/prestadores */}
                      {visitorStats && personData.roles?.some((r: any) => r.role_type === 'visitor' || r.role_type === 'service_provider') && (
                        <div className="text-center mt-1">
                          <span className={cn("text-xs", darkMode ? "text-gray-500" : "text-gray-400")}>
                            {visitorStats.total_visits} {visitorStats.total_visits === 1 ? 'visita' : 'visitas'}
                            {visitorStats.last_visit_date && (() => {
                              const d = new Date(visitorStats.last_visit_date);
                              const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
                              const label = diff === 0 ? 'Hoje' : diff === 1 ? 'Ontem' : diff < 7 ? `${diff}d atrás` : d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
                              return ` · ${label}`;
                            })()}
                          </span>
                        </div>
                      )}

                      {/* Entrada / Saída manual — for residents/staff, who don't go through the visitor flow */}
                      {(() => {
                        const activeResidentStaffRole = personData.roles?.find((r: any) =>
                          r.is_active && (r.role_type === 'resident' || r.role_type === 'staff' || r.role_type === 'condo_employee')
                        );
                        if (!activeResidentStaffRole) return null;
                        return (
                          <div className="w-full mt-1.5">
                            <div className={cn("text-[10px] font-medium uppercase tracking-wide text-center mb-1", darkMode ? "text-gray-500" : "text-gray-400")}>
                              Registro Manual
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => handleManualDirectionalClick('entrada')}
                                className={cn("flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors",
                                  darkMode ? "border-gray-700 text-gray-300 hover:bg-gray-800" : "border-gray-200 text-gray-600 hover:bg-gray-50"
                                )}>
                                <LogIn className="h-3 w-3" />
                                Entrada
                              </button>
                              <button onClick={() => handleManualDirectionalClick('saida')}
                                className={cn("flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors",
                                  darkMode ? "border-gray-700 text-gray-300 hover:bg-gray-800" : "border-gray-200 text-gray-600 hover:bg-gray-50"
                                )}>
                                <LogOut className="h-3 w-3" />
                                Saída
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

              </div>

              {/* Vertical Navigation */}
              <nav className={cn("border-t", darkMode ? "border-gray-800" : "border-gray-100")}>
                {loading ? (
                  <div className="px-4 py-3 space-y-2.5">
                    {[56, 72, 64, 48, 80, 64, 40, 48, 72].map((w, i) => (
                      <div key={i} className={cn("h-3 rounded animate-pulse", darkMode ? "bg-gray-700" : "bg-gray-200")} style={{ width: w }} />
                    ))}
                  </div>
                ) : (
                  tabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => {
                          if (isEditing && tab.id !== activeTab) {
                            setPendingTab(tab.id);
                          } else {
                            setActiveTab(tab.id);
                          }
                        }}
                        className={cn(
                          "w-full text-left px-4 py-2.5 text-[11px] font-bold tracking-widest uppercase transition-all border-l-2",
                          isActive
                            ? darkMode
                              ? "border-blue-500 text-white bg-gray-800/60"
                              : "border-gray-900 text-gray-900 bg-gray-50"
                            : darkMode
                              ? "border-transparent text-gray-500 hover:text-gray-300 hover:bg-gray-800/30"
                              : "border-transparent text-gray-400 hover:text-gray-700 hover:bg-gray-50/70"
                        )}
                      >
                        {tab.label}
                      </button>
                    );
                  })
                )}
              </nav>
                </div>{/* end scrollable top */}

              {loading ? (
                <div className={cn("flex-shrink-0 px-4 pb-4 pt-3 border-t space-y-2", darkMode ? "border-gray-800" : "border-gray-100")}>
                  <div className={cn("h-2.5 w-16 rounded animate-pulse", darkMode ? "bg-gray-700" : "bg-gray-200")} />
                  <div className="flex gap-1.5">
                    <div className={cn("flex-1 h-7 rounded-lg animate-pulse", darkMode ? "bg-gray-700" : "bg-gray-200")} />
                    <div className={cn("flex-1 h-7 rounded-lg animate-pulse", darkMode ? "bg-gray-700" : "bg-gray-200")} />
                  </div>
                </div>
              ) : personData && !readOnly && (
                <div className={cn("flex-shrink-0 px-4 pb-4 pt-3 border-t", darkMode ? "border-gray-800" : "border-gray-100")}>
                  <p className={cn("text-[10px] uppercase tracking-widest font-semibold mb-2", darkMode ? "text-gray-500" : "text-gray-400")}>
                    Dispositivos
                  </p>
                  {/* A matched pair: same shape, size and weight, differing only in accent.
                      A solid blue fill beside a pale red tint read as two unrelated controls
                      and pulled the eye to "Sincronizar" as if it were the page's primary
                      action, in a sidebar whose whole language is quiet all-caps labels. */}
                  <div className="flex gap-1.5">
                    <button
                      onClick={handleSyncPerson}
                      disabled={syncStatus.isSyncing}
                      className={cn(
                        "flex-1 px-2 py-1.5 text-[10px] font-bold tracking-widest uppercase rounded-lg border transition-colors",
                        syncStatus.isSyncing
                          ? "border-transparent bg-gray-100 text-gray-400 cursor-not-allowed"
                          : darkMode
                            ? "border-blue-900/40 bg-blue-900/20 text-blue-400 hover:bg-blue-900/40"
                            : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      )}
                    >
                      {syncStatus.isSyncing ? '...' : 'Sincronizar'}
                    </button>
                    <button
                      onClick={handleRevokeDevices}
                      className={cn(
                        "flex-1 px-2 py-1.5 text-[10px] font-bold tracking-widest uppercase rounded-lg border transition-colors",
                        darkMode
                          ? "border-red-900/40 bg-red-900/20 text-red-400 hover:bg-red-900/40"
                          : "border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                      )}
                    >
                      Revogar
                    </button>
                  </div>
                </div>
              )}
            </div>

              {/* Content Area */}
              <div className="flex-1 min-w-0 p-5 overflow-y-auto">

                {/* Tab Content */}
                <div className="min-h-[300px]">
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className={cn(
                        "animate-spin rounded-full h-10 w-10 border-b-2",
                        darkMode ? "border-blue-500" : "border-black"
                      )}></div>
                    </div>
                  ) : error ? (
                    <div className="flex flex-col items-center justify-center h-64 gap-4">
                      <p className="text-red-600">{error}</p>
                      <button
                        onClick={() => personId && fetchPersonDetails(personId)}
                        className={cn(
                          "px-4 py-2 rounded-lg transition-colors",
                          darkMode
                            ? "bg-blue-600 text-white hover:bg-blue-700"
                            : "bg-black text-white hover:bg-gray-800"
                        )}
                      >
                        Tentar Novamente
                      </button>
                    </div>
                  ) : personData ? (
                    <>
                      {activeTab === 'overview' && <OverviewTab person={personData} onRefresh={() => personId && fetchPersonDetails(personId)} isEditing={isEditing} onEditingChange={setIsEditing} darkMode={darkMode} onViewAllNotes={() => setActiveTab('notes')} onShowHistory={() => setShowHistoryModal(true)}
                        /* Same revoke flow the visits list uses — reason prompt, ledger entry and
                           device clean-up included — addressed by ROLE, because a person holds
                           several clearances at once. */
                        onRevokeRole={(role) => handleRevokeAccess({
                          person_id: personData.id,
                          person_name: `${personData.first_name || ''} ${personData.last_name || ''}`.trim(),
                          role_id: role.id,
                        })} />}
                      {activeTab === 'permissions' && <PermissionsTab person={personData} onRefresh={() => personId && fetchPersonDetails(personId)} readOnly={readOnly} darkMode={darkMode} syncStatus={syncStatus} setSyncStatus={setSyncStatus} showConfirmModal={showConfirmModalForPermissions} />}
                      {activeTab === 'history' && <AccessHistoryTab personId={personData.id} personName={`${personData.first_name || ''} ${personData.last_name || ''}`.trim() || undefined} personPhotoUrl={personData.profile_picture_url || personData.identity_photo_url} darkMode={darkMode} />}
                      {activeTab === 'photos' && <IdentityPhotosTab person={personData} onRefresh={() => personId && fetchPersonDetails(personId)} onPhotoClick={openPhotoViewer} readOnly={effectiveReadOnly} darkMode={darkMode} triggerAdd={triggerPhotoAdd} onTriggerAddConsumed={() => setTriggerPhotoAdd(false)} />}
                      {activeTab === 'documents' && (
                        <DocumentsTab
                          person={personData}
                          onRefresh={() => personId && fetchPersonDetails(personId)}
                          onPhotoClick={openPhotoViewer}
                          onEditPhoto={(photo) => photoFlowRef.current?.update(photo)}
                          onNewPhoto={(photoType) => photoFlowRef.current?.create({ photoType })}
                          readOnly={readOnly}
                          darkMode={darkMode}
                        />
                      )}
                      {activeTab === 'vehicles' && <VehiclesTab person={personData} onRefresh={() => personId && fetchPersonDetails(personId)} darkMode={darkMode} />}
                      {activeTab === 'tags' && <TagsTab person={personData} onRefresh={() => personId && fetchPersonDetails(personId)} darkMode={darkMode} />}
                      {activeTab === 'notes' && <NotesTab person={personData} darkMode={darkMode} />}
                      {activeTab === 'aso' && showASOTab && personData.customer?.id && (
                        <ASOTab person={personData} customerId={personData.customer.id} darkMode={darkMode} />
                      )}
                      {activeTab === 'biometria' && personData.customer?.id && (
                        <BiometriaTab person={personData} customerId={personData.customer.id} darkMode={darkMode} />
                      )}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab-switch Confirmation Modal (shown when editing and user clicks another tab) */}
      {pendingTab && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[85]"
          onClick={() => setPendingTab(null)}
        >
          <div
            className={cn(
              "max-w-sm w-full mx-4 p-5 rounded-lg shadow-xl",
              darkMode ? "bg-gray-800" : "bg-white"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              <h3 className={cn(
                "text-base font-semibold mb-1.5",
                darkMode ? "text-gray-100" : "text-gray-900"
              )}>
                Descartar alterações?
              </h3>
              <p className={cn(
                "text-sm",
                darkMode ? "text-gray-300" : "text-gray-600"
              )}>
                Você está editando informações. Ao trocar de aba, as alterações não salvas serão perdidas.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingTab(null)}
                className={cn(
                  "text-sm px-3 py-1.5 rounded-md transition-colors",
                  darkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-100" : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                )}
              >
                Continuar Editando
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setActiveTab(pendingTab);
                  setPendingTab(null);
                }}
                className="text-sm px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
              >
                Descartar e Trocar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Confirmation Modal */}
      {showCloseConfirm && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[85]"
          onClick={() => setShowCloseConfirm(false)}
        >
          <div
            className={cn(
              "max-w-md w-full mx-4 p-6 rounded-lg shadow-xl",
              darkMode ? "bg-gray-800" : "bg-white"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              <h3 className={cn(
                "text-lg font-semibold mb-2",
                darkMode ? "text-gray-100" : "text-gray-900"
              )}>
                Descartar alterações?
              </h3>
              <p className={cn(
                "text-sm",
                darkMode ? "text-gray-300" : "text-gray-600"
              )}>
                Você está editando informações. Se fechar agora, as alterações não salvas serão perdidas.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCloseConfirm(false)}
                className={cn(
                  "px-4 py-2 rounded-lg transition-colors",
                  darkMode
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                )}
              >
                Continuar Editando
              </button>
              <button
                onClick={handleForceClose}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Descartar e Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Viewer Modal */}
      <ManualAccessDialog
        open={manualAccess.open}
        direction={manualAccess.direction}
        personName={`${personData?.first_name || ''} ${personData?.last_name || ''}`.trim()}
        vehicles={personData?.vehicles as any}
        isProcessing={manualAccess.isProcessing}
        darkMode={darkMode}
        onCancel={() => setManualAccess(prev => ({ ...prev, open: false }))}
        onConfirm={submitManualAccess}
      />

      {photoViewerOpen && currentPhoto && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/90 p-4"
          onClick={closePhotoViewer}
        >
          {/* Modal Container */}
          <div
            className="relative max-w-[85vw] max-h-[85vh] flex flex-col items-center justify-center bg-gray-900 rounded-xl shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button - Inside modal container for easier access */}
            <button
              onClick={closePhotoViewer}
              className="absolute top-3 right-3 z-10 p-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-lg"
              aria-label="Fechar visualizador de fotos"
              title="Fechar (ESC)"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Navigation - Left Arrow */}
            {photoGallery.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPreviousPhoto();
                }}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors shadow-lg"
                aria-label="Foto anterior"
                title="Foto anterior (←)"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}

            {/* Navigation - Right Arrow */}
            {photoGallery.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNextPhoto();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors shadow-lg"
                aria-label="Próxima foto"
                title="Próxima foto (→)"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}

            {/* Photo Container */}
            <div className="flex flex-col items-center justify-center w-full h-full">
              {/* Photo */}
              <div className="relative flex items-center justify-center min-w-[200px] min-h-[200px]">
                {!lightboxPhotoLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-white/50 animate-spin" />
                  </div>
                )}
                {/* Zoom shows the untouched upload where there is one: the crop is sized for
                    an avatar, and what someone opening a photo full-screen wants is the whole
                    frame. If the original 404s (storage lost it, or a stale URL) the crop is
                    still there to fall back to, before any "unavailable" placeholder. */}
                <img
                  key={lightboxShownUrl}
                  src={lightboxShownUrl}
                  alt={currentPhoto.caption}
                  className={cn("max-w-full max-h-[60vh] object-contain rounded-lg shadow-xl transition-opacity", !lightboxPhotoLoaded && "opacity-0")}
                  onLoad={() => setLightboxPhotoLoaded(true)}
                  onError={(e) => {
                    if (lightboxShownUrl !== currentPhoto.url) {
                      setLightboxOriginalFailed(true);
                      return;
                    }
                    setLightboxPhotoLoaded(true);
                    const target = e.target as HTMLImageElement;
                    target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ddd" width="400" height="400"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle" font-family="sans-serif" font-size="18"%3EImagem não disponível%3C/text%3E%3C/svg%3E';
                  }}
                />
                {/* Action buttons — bottom overlay strip on the photo */}
                {!effectiveReadOnly && currentPhoto?.photoId && lightboxPhotoLoaded && (
                  <div className="absolute bottom-0 inset-x-0 flex rounded-b-lg overflow-hidden">
                    <button
                      onClick={(e) => { e.stopPropagation(); openPhotoEdit(currentPhoto); }}
                      className="flex-1 py-2.5 text-xs font-bold tracking-widest text-white/90 bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-colors border-r border-white/10"
                    >
                      ATUALIZAR
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPhotoViewerOpen(false);
                        photoFlowRef.current?.create({ url: currentPhoto.url, photoType: currentPhoto.photoType });
                      }}
                      className="flex-1 py-2.5 text-xs font-bold tracking-widest text-white/90 bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-colors"
                    >
                      NOVA FOTO
                    </button>
                  </div>
                )}
              </div>

              {/* Photo Metadata */}
              <div className="mt-4 text-center space-y-2">
                {/* Person Name */}
                {personData && (
                  <p className="text-white text-lg font-semibold">
                    {personData.first_name} {personData.last_name}
                  </p>
                )}

                {/* Photo Caption and Counter */}
                <div className="flex items-center justify-center gap-3 text-white/80">
                  <span className="text-sm">{currentPhoto.caption}</span>
                  {currentPhoto.isPrimary && (
                    <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-300 rounded-full text-xs">
                      Principal
                    </span>
                  )}
                  {photoGallery.length > 1 && (
                    <span className="text-sm">
                      {currentPhotoIndex + 1} / {photoGallery.length}
                    </span>
                  )}
                </div>

                {/* Upload Date */}
                {currentPhoto.uploadDate && (
                  <p className="text-white/60 text-xs">
                    Enviada em {new Date(currentPhoto.uploadDate).toLocaleDateString('pt-BR')}
                  </p>
                )}
              </div>

              {/* Keyboard Hints */}
              {photoGallery.length > 1 && (
                <div className="mt-3 text-white/40 text-xs text-center">
                  Use as setas ← → para navegar | ESC para fechar
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* The one photo capture flow — driven by the lightbox, the sidebar and the Documentos tab */}
      {personData && (
        <PhotoCaptureFlow
          ref={photoFlowRef}
          personId={personData.id}
          customerId={personData.customer?.id}
          onSaved={() => personId && fetchPersonDetails(personId)}
          darkMode={darkMode}
        />
      )}

      {/* Nova Visita Modal */}
      {showVisitorModal && personData && (() => {
        // Extract document photos from identity_photos array (same as DocumentsTab)
        const documentPhotos = personData.identity_photos?.filter(p => p.photo_type?.startsWith('document')) || [];
        const documentFrontFromPhotos = documentPhotos.find(p => p.photo_type === 'document_front')?.photo_url;
        const documentBackFromPhotos = documentPhotos.find(p => p.photo_type === 'document_back')?.photo_url;

        // Use photos from identity_photos array, fallback to legacy fields
        const documentFrontUrl = documentFrontFromPhotos || personData.document_front_url;
        const documentBackUrl = documentBackFromPhotos || personData.document_back_url;

        console.log(' PersonDetailModal personData for Nova Visita:', {
          id: personData.id,
          name: `${personData.first_name} ${personData.last_name}`,
          identity_photo_url: personData.identity_photo_url,
          profile_picture_url: personData.profile_picture_url,
          document_front_url: documentFrontUrl,
          document_back_url: documentBackUrl,
          vehicles: personData.vehicles,
          identity_photos_count: personData.identity_photos?.length,
        });
        return (
          <VisitorRegistrationModal
            open={showVisitorModal}
            onClose={() => setShowVisitorModal(false)}
            customerId={personData.customer?.id || ''}
            selectedCustomer={personData.customer ? {
              type: personData.customer.type,
              name: personData.customer.name,
              condoType: personData.customer.condo_data?.condo_type === 'casas' ? 'casa' : undefined,
              mobileAppEnabled: !!((personData.customer as any).condo_data?.mobile_app_enabled || (personData.customer as any).empresa_data?.mobile_app_enabled),
            } : null}
            initialStep="form"
            prefilledPerson={{
              id: personData.id,
              first_name: personData.first_name,
              last_name: personData.last_name,
              cpf: personData.cpf,
              phone: personData.phone,
              identity_photo_url: personData.identity_photo_url,
              profile_picture_url: personData.profile_picture_url,
              document_front_url: documentFrontUrl,
              document_back_url: documentBackUrl,
              vehicles: personData.vehicles,
            }}
            onSuccess={() => {
            console.log(' PersonDetailModal: Visit registration successful');
            setShowVisitorModal(false);

            // Refresh visitor stats for this person
            const customerId = personData.customer?.id;
            if (customerId && personId) {
              condoApi.getPersonVisitorStats(customerId, personId).then(setVisitorStats).catch(console.error);
            }

            // Trigger global refresh (updates "visitantes do dia" list in OperationalView)
            // Use setTimeout to ensure backend has processed the new visit
            setTimeout(() => {
              console.log(' PersonDetailModal: Triggering global refresh via onPersonUpdate');
              if (onPersonUpdate && personId) {
                onPersonUpdate(personId);
              }
            }, 500);

            // Close the PersonDetailModal after refresh is triggered
            setTimeout(() => {
              console.log(' PersonDetailModal: Closing person detail modal');
              closePersonDetail();
            }, 800);
          }}
          darkMode={darkMode}
          compactMode={true}
        />
        );
      })()}

      {/* Modification History Modal */}
      {showHistoryModal && personData && (
        <ModificationHistoryModal
          personId={personData.id}
          personName={`${personData.first_name} ${personData.last_name}`}
          onClose={() => setShowHistoryModal(false)}
          darkMode={darkMode}
        />
      )}

      {/* Reentry Confirmation Modal */}
      <ReentryConfirmationModal
        isOpen={showReentryConfirmation}
        onClose={handleReentryCancel}
        onConfirm={handleReentryConfirm}
        visitorInfo={reentryVisitorInfo ? {
          personName: reentryVisitorInfo.personName,
          unit: reentryVisitorInfo.unit,
          entryTime: reentryVisitorInfo.entryTime,
        } : undefined}
      />

      {/* Operational Confirm Modal - Higher z-index to appear above PersonDetailModal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-[95]">
          <OperationalConfirmModal
            isOpen={confirmModal.isOpen}
            onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            onConfirm={confirmModal.onConfirm}
            title={confirmModal.title}
            message={confirmModal.message}
            confirmText={confirmModal.confirmText}
            confirmButtonClass={confirmModal.confirmButtonClass}
            icon={confirmModal.icon}
            isProcessing={confirmModal.isProcessing}
            showDisclaimer={confirmModal.showDisclaimer}
            disclaimerType={confirmModal.disclaimerType}
            operatorName={confirmModal.operatorName}
            requireAcknowledgment={confirmModal.requireAcknowledgment}
          />
        </div>
      )}

      {/* Revoke Reason Modal - independent of confirmModal */}
      <RevokeReasonModal
        isOpen={revokeModal.isOpen}
        visitor={revokeModal.visitor ? { name: revokeModal.visitor.person_name } : null}
        operatorName={operatorName}
        onClose={() => setRevokeModal({ isOpen: false, visitor: null })}
        onConfirm={handleRevokeConfirm}
        darkMode={darkMode}
      />
    </>
  );
};
